import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ChevronDown, Clock, Globe, Mail } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { BookButton } from "./BookButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  full_name: "",
  email: "",
  company: "",
  inquiry_type: "Consultation",
  message: "",
};

const INQUIRY_TYPES = [
  "Consultation",
  "Security Audit",
  "Use Case Engineering",
  "Playbook Design",
  "Other",
];

const DETAILS = [
  {
    icon: Mail,
    label: "Email us",
    value: "contact@gentecit.com",
    href: "mailto:contact@gentecit.com",
    tid: "contact-email-link",
  },
  { icon: Clock, label: "Response", value: "24/7 enterprise emergency response" },
  { icon: Globe, label: "Coverage", value: "Global — remote consulting & on-site scoping" },
];

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-all duration-200 focus:border-cyan-300/60 focus:bg-white/[0.05] focus:ring-2 focus:ring-cyan-400/20";

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const onPreset = (e) =>
      setForm((f) => ({ ...f, inquiry_type: e.detail || "Consultation" }));
    window.addEventListener("gentec:preset-inquiry", onPreset);
    return () => window.removeEventListener("gentec:preset-inquiry", onPreset);
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry received — our team will respond shortly.", {
        icon: <CheckCircle2 className="h-4 w-4 text-emerald-400" />,
      });
      setForm(INITIAL);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Something went wrong. Email us at contact@gentecit.com."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-20 sm:py-28 lg:py-32"
    >
      <div className="pointer-events-none absolute bottom-0 right-0 h-[380px] w-[560px] rounded-full bg-emerald-500/7 blur-[140px]" />
      <div className="container relative grid gap-14 lg:grid-cols-2 lg:gap-20">
        {/* Left: pitch + direct details */}
        <div>
          <SectionHeading
            index="03"
            label="Contact"
            title="Let's secure what you're building."
          />
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
              Tell us about your environment, your team, and the gaps keeping
              you up at night. We'll come back with a scoped, pragmatic plan —
              no boilerplate decks.
            </p>
          </Reveal>

          <div className="mt-10 space-y-3">
            {DETAILS.map((d, i) => (
              <Reveal key={d.label} delay={0.12 + i * 0.07}>
                <div className="flex items-center gap-4 rounded-2xl border border-white/8 bg-white/[0.02] px-5 py-4 transition-colors duration-300 hover:border-cyan-300/25">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-emerald-300/20 bg-emerald-400/8 text-emerald-300">
                    <d.icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                      {d.label}
                    </p>
                    {d.href ? (
                      <a
                        href={d.href}
                        data-testid={d.tid}
                        className="font-display text-sm font-bold text-slate-100 transition-colors hover:text-cyan-300"
                      >
                        {d.value}
                      </a>
                    ) : (
                      <p className="font-display text-sm font-bold text-slate-100">
                        {d.value}
                      </p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Right: inquiry form */}
        <Reveal delay={0.15}>
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            className="rounded-3xl border border-white/10 bg-panel/70 p-6 shadow-[0_30px_90px_rgba(0,0,0,0.4)] sm:p-8"
          >
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-1">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Full Name *
                </label>
                <input
                  required
                  data-testid="contact-input-name"
                  value={form.full_name}
                  onChange={set("full_name")}
                  placeholder="Jane Architect"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Work Email *
                </label>
                <input
                  required
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={set("email")}
                  placeholder="jane@company.com"
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-1">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Company
                </label>
                <input
                  data-testid="contact-input-company"
                  value={form.company}
                  onChange={set("company")}
                  placeholder="Acme Corp"
                  className={inputCls}
                />
              </div>
              <div className="relative sm:col-span-1">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Inquiry Type
                </label>
                <select
                  data-testid="contact-select-inquiry-type"
                  value={form.inquiry_type}
                  onChange={set("inquiry_type")}
                  className={`${inputCls} appearance-none pr-10`}
                >
                  {INQUIRY_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute bottom-3.5 right-3.5 h-4 w-4 text-slate-500" />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                  Project Overview / Scoping Needs *
                </label>
                <textarea
                  required
                  rows={5}
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="What are you protecting, and what's keeping you up at night?"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>

            <BookButton
              testid="contact-form-submit"
              label={sending ? "Sending…" : "Send Inquiry"}
              className="mt-6 w-full py-4"
            />
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Encrypted in transit — read by humans only
            </p>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
